import { useCallback, useEffect, useState } from 'react';
import type { CategoriesResponse, CompanySheet } from '@/types/category';
import http from '@/services/http';
import { accountApiPath } from '@/config/deutscheBankDemo';

export function useCategories(accountId: number | null) {
  const [categories, setCategories] = useState<CategoriesResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountId) return;

    setLoading(true);
    http
      .get(accountApiPath(accountId, '/categories'))
      .then((res) => setCategories(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [accountId]);

  return { categories, loading };
}

export function useCategorySheets(accountId: number | null, categoryId: number | null) {
  const [sheets, setSheets] = useState<CompanySheet[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountId || !categoryId) {
      setSheets([]);
      return;
    }

    setLoading(true);
    http
      .get(accountApiPath(accountId, `/categories/${categoryId}/sheets`))
      .then((res) => setSheets(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [accountId, categoryId]);

  return { sheets, loading };
}

export function useFilteredSheets(accountId: number | null, selectedCategoryIds: number[]) {
  const [sheets, setSheets] = useState<CompanySheet[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSheets = useCallback(async () => {
    if (!accountId || selectedCategoryIds.length === 0) {
      setSheets([]);
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        selectedCategoryIds.map((catId) =>
          http
            .get(accountApiPath(accountId, `/categories/${catId}/sheets`))
            .then((res) => res.data as CompanySheet[])
        )
      );

      const merged = new Map<number, CompanySheet>();
      for (const list of results) {
        for (const sheet of list) {
          merged.set(sheet.id, sheet);
        }
      }
      setSheets(Array.from(merged.values()));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId, selectedCategoryIds]);

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  return { sheets, loading };
}
